/**
 * Schedule writes from a phone: one shift, an act, or a person's rest of the
 * day — without sending (and so overwriting) the whole schedule.
 *
 * The laptop editor saves the FULL schedule (replace_crew_assignments: what
 * the payload lacks is switched off). A phone sends ops instead, to
 * public.patch_crew_assignments (migration 20261021120100), which applies up
 * to MAX_SCHEDULE_OPS of them atomically, in order. Every op on an existing
 * row carries the `updated_at` the phone read: if someone changed the row
 * since, the whole call fails with a ScheduleConflictError (SQLSTATE PT409)
 * and nothing is written.
 *
 * Each successful patch bumps briefings.schedule_revision. The laptop checks
 * that number with assertScheduleRevision before its own full-set save, and
 * each of its schedule writes checks it again atomically with the write
 * (replace_lineup_at_revision, save-schedule-changes →
 * replace_crew_assignments_at_revision, apply_assignment_time_patches_at_revision,
 * all under the per-briefing lock a patch holds), so a laptop that loaded the
 * schedule before the phone edit stops instead of silently reverting it.
 *
 * The op builders are pure. They hold the client side of the database's
 * rules where the caller has the facts: an artist shift never carries a
 * location (crew_assignments_null_location_for_artist) and its stage follows
 * the lineup; a start moved across the 06:00 festival-day rollover must say
 * which festival day it is on.
 */

import { z } from "zod";
import { FESTIVAL_DAY_ROLLOVER_HOUR } from "./eventDays";

/** patch_crew_assignments refuses more ops than this in one call. */
export const MAX_SCHEDULE_OPS = 50;

/**
 * SQLSTATE of the optimistic-concurrency conflict (PostgREST answers it with
 * HTTP 409). Deliberately not 40001 (serialization_failure): PostgREST retries
 * a transaction that fails with 40001 by itself, without end, even after the
 * client gave up. Twin of the ERRCODE in migration 20261021120100.
 */
export const SCHEDULE_CONFLICT_CODE = "PT409";

// ─── Op shapes (the jsonb patch_crew_assignments reads) ─────────────

/** What an op needs from a crew_assignments row it changes. */
export interface PatchableAssignment {
  id: string;
  /** crew_assignments.updated_at exactly as read — the concurrency token. */
  updated_at: string;
}

/** Times / day of a shift. Times are "HH:MM" or "HH:MM:SS", dates "yyyy-MM-dd". */
export interface AssignmentTimePatch {
  filming_start?: string;
  filming_end?: string;
  event_date?: string;
}

/**
 * Every field an 'update' op may change. null clears notes/location/subject/
 * stage; a custom block without a stage is filed under "Other", the laptop's
 * no-stage label.
 */
export interface AssignmentUpdatePatch extends AssignmentTimePatch {
  stage_id?: string | null;
  notes?: string | null;
  location?: string | null;
  subject?: string | null;
}

export type AssignmentType = "artist" | "custom";

/** A new shift. An artist shift takes its stage (and artist) from `lineup_id`. */
export interface AssignmentInsertRow {
  crew_member_id: string;
  event_date: string;
  filming_start: string;
  filming_end: string;
  assignment_type: AssignmentType;
  artist_name?: string | null;
  subject?: string | null;
  stage_id?: string | null;
  lineup_id?: string | null;
  notes?: string | null;
  location?: string | null;
}

export type ScheduleOp =
  | { op: "update"; id: string; expected_updated_at: string; patch: AssignmentUpdatePatch }
  | { op: "insert"; row: AssignmentInsertRow }
  | { op: "deactivate"; id: string; expected_updated_at: string }
  | { op: "swap"; id: string; expected_updated_at: string; new_crew_member_id: string }
  | {
      op: "lineup_time";
      lineup_id: string;
      expected_updated_at: string;
      set_start: string;
      set_end: string;
    };

// ─── Results ────────────────────────────────────────────────────────

const opResultSchema = z.object({
  op_index: z.number().int(),
  status: z.enum(["updated", "inserted", "deactivated", "swapped", "lineup_moved", "skipped"]),
  /** The row the op ended on: for 'swap' the NEW row, for 'lineup_time' the lineup slot. */
  id: z.string().optional(),
  /** Its new concurrency token, for a follow-up op. */
  updated_at: z.string().optional(),
  /**
   * Why an op was skipped: the crew member already has this exact block of
   * their own (an assistant's mirror in the slot makes way instead), nothing
   * changed, or already off.
   */
  reason: z.enum(["duplicate", "unchanged", "already_inactive"]).optional(),
});

const patchResultSchema = z.object({
  revision: z.number().int(),
  results: z.array(opResultSchema),
});

export type ScheduleOpResult = z.infer<typeof opResultSchema>;
export type PatchCrewAssignmentsResult = z.infer<typeof patchResultSchema>;

// ─── Rules ──────────────────────────────────────────────────────────

const SECONDS_PER_DAY = 24 * 60 * 60;
const ROLLOVER_SECONDS = FESTIVAL_DAY_ROLLOVER_HOUR * 60 * 60;

/** "HH:MM" / "HH:MM:SS" → seconds since midnight; NaN when unreadable. */
function clockSeconds(time: string): number {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(time.trim());
  if (!match) return Number.NaN;
  const [, h, m, s = "0"] = match;
  return Number(h) * 3600 + Number(m) * 60 + Number(s);
}

/** Seconds since the festival day began (06:00), 0 … 86399. */
function secondsIntoFestivalDay(time: string): number {
  return (clockSeconds(time) - ROLLOVER_SECONDS + SECONDS_PER_DAY) % SECONDS_PER_DAY;
}

/**
 * Does moving a shift's start from `oldStart` to `newStart` (same event_date)
 * cross the 06:00 festival-day rollover? A festival day runs 06:00 → 05:59,
 * so 05:30 → 06:30 is 23 hours EARLIER, on the other end of the day — while
 * 23:00 → 01:00 (midnight) is simply two hours later. Crossing = the start
 * moves the short way round the clock through 06:00, i.e. more than 12 hours
 * within the festival day.
 *
 * TS twin of public._crosses_festival_day_rollover (migration 20261021120100).
 * When true, the update must carry an explicit event_date, or the database
 * refuses it.
 */
export function crossesFestivalDayRollover(oldStart: string, newStart: string): boolean {
  const delta = secondsIntoFestivalDay(newStart) - secondsIntoFestivalDay(oldStart);
  return Math.abs(delta) > SECONDS_PER_DAY / 2;
}

// ─── Builders ───────────────────────────────────────────────────────

/** Drop keys whose value is undefined: "absent" and "set to null" differ in a patch. */
function definedOnly<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

/**
 * Change a shift's times (and, when it moves across 06:00 or to another
 * festival day, its event_date).
 *
 * Pass the row's current `filming_start` to have a rollover crossing caught
 * here, before the round trip; the database checks it either way.
 */
export function buildTimeUpdate(
  assignment: PatchableAssignment & { filming_start?: string },
  times: AssignmentTimePatch,
): ScheduleOp {
  const patch = definedOnly({
    filming_start: times.filming_start,
    filming_end: times.filming_end,
    event_date: times.event_date,
  });
  if (Object.keys(patch).length === 0) {
    throw new RangeError("buildTimeUpdate: nothing to change");
  }
  if (
    assignment.filming_start &&
    patch.filming_start &&
    !patch.event_date &&
    crossesFestivalDayRollover(assignment.filming_start, patch.filming_start)
  ) {
    throw new RangeError(
      `buildTimeUpdate: ${assignment.filming_start} → ${patch.filming_start} crosses the 06:00 ` +
        "festival-day rollover; pass event_date",
    );
  }
  return {
    op: "update",
    id: assignment.id,
    expected_updated_at: assignment.updated_at,
    patch,
  };
}

/**
 * Change any patchable field of a shift. On an artist shift the location is
 * dropped (artist shifts never carry one) and a stage change is refused (its
 * stage follows the lineup slot).
 */
export function buildUpdate(
  assignment: PatchableAssignment & { assignment_type: string; stage_id?: string | null },
  patch: AssignmentUpdatePatch,
): ScheduleOp {
  const isArtist = assignment.assignment_type === "artist";
  const cleaned = definedOnly({ ...patch, location: isArtist ? undefined : patch.location });
  if (
    isArtist &&
    cleaned.stage_id !== undefined &&
    cleaned.stage_id !== (assignment.stage_id ?? null)
  ) {
    throw new RangeError("buildUpdate: an artist shift's stage follows the lineup");
  }
  if (Object.keys(cleaned).length === 0) {
    throw new RangeError("buildUpdate: nothing to change");
  }
  return {
    op: "update",
    id: assignment.id,
    expected_updated_at: assignment.updated_at,
    patch: cleaned,
  };
}

/** A new shift. An artist shift never carries a location. */
export function buildInsert(row: AssignmentInsertRow): ScheduleOp {
  const isArtist = row.assignment_type === "artist";
  return {
    op: "insert",
    row: definedOnly({ ...row, location: isArtist ? undefined : row.location }),
  };
}

/** Switch a shift off (it is kept, inactive — never deleted). */
export function buildDeactivate(assignment: PatchableAssignment): ScheduleOp {
  return { op: "deactivate", id: assignment.id, expected_updated_at: assignment.updated_at };
}

/**
 * Give a shift to another crew member of the same festival day. The server
 * copies every column (assistants, mirror opt-out, location, notes, lineup
 * slot …) to a new row and switches the old one off; the op result's `id` is
 * the NEW row (for the whole-day assistant who mirrors the shift: their
 * mirror row, which becomes the copy).
 */
export function buildSwap(assignment: PatchableAssignment, newCrewMemberId: string): ScheduleOp {
  return {
    op: "swap",
    id: assignment.id,
    expected_updated_at: assignment.updated_at,
    new_crew_member_id: newCrewMemberId,
  };
}

/**
 * Move an artist's set in the lineup. Its crew shifts do not follow by
 * themselves: send their buildTimeUpdate ops in the same call ("the act runs
 * late").
 */
export function buildLineupTime(
  slot: { id: string; updated_at: string },
  setStart: string,
  setEnd: string,
): ScheduleOp {
  return {
    op: "lineup_time",
    lineup_id: slot.id,
    expected_updated_at: slot.updated_at,
    set_start: setStart,
    set_end: setEnd,
  };
}

// ─── Errors ─────────────────────────────────────────────────────────

/** A schedule write the database refused. `code` is the SQLSTATE. */
export class ScheduleWriteError extends Error {
  readonly code: string | null;

  constructor(message: string, code: string | null) {
    super(message);
    this.name = "ScheduleWriteError";
    this.code = code;
  }
}

/**
 * Someone changed the schedule since it was read (SQLSTATE PT409). Nothing
 * was written. Reload; never retry automatically — the same stale token fails
 * the same way (see apps/web/src/lib/saveConflictBreaker.ts for what an
 * unbounded retry on this conflict did to production).
 */
export class ScheduleConflictError extends ScheduleWriteError {
  constructor(message: string) {
    super(message, SCHEDULE_CONFLICT_CODE);
    this.name = "ScheduleConflictError";
  }
}

/** True for a ScheduleConflictError and for a raw PostgREST error with code PT409. */
export function isScheduleConflict(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { code?: unknown }).code === SCHEDULE_CONFLICT_CODE
  );
}

// ─── Calls ──────────────────────────────────────────────────────────

/** The one method these calls need; the web and mobile Supabase clients both fit. */
export interface ScheduleRpcClient {
  rpc(
    fn: string,
    args: Record<string, unknown>,
  ): PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>;
}

function toWriteError(error: { message: string; code?: string }): ScheduleWriteError {
  return isScheduleConflict(error)
    ? new ScheduleConflictError(error.message)
    : new ScheduleWriteError(error.message, error.code ?? null);
}

export interface PatchCrewAssignmentsInput {
  briefingId: string;
  ops: ScheduleOp[];
  /**
   * A fresh UUID per user action, reused on every retry of that action: the
   * database then applies it once and answers retries with the first result.
   */
  clientOpId?: string;
}

/**
 * Apply schedule ops (all or nothing). Throws ScheduleConflictError when a
 * row changed since it was read, ScheduleWriteError for any other refusal.
 */
export async function patchCrewAssignments(
  client: ScheduleRpcClient,
  { briefingId, ops, clientOpId }: PatchCrewAssignmentsInput,
): Promise<PatchCrewAssignmentsResult> {
  if (ops.length === 0 || ops.length > MAX_SCHEDULE_OPS) {
    throw new RangeError(`patchCrewAssignments: 1 to ${MAX_SCHEDULE_OPS} ops, got ${ops.length}`);
  }
  const { data, error } = await client.rpc("patch_crew_assignments", {
    p_briefing_id: briefingId,
    p_ops: ops,
    p_client_op_id: clientOpId ?? null,
  });
  if (error) throw toWriteError(error);
  return patchResultSchema.parse(data);
}

/**
 * The laptop's check before a full-set schedule save: resolves with the
 * revision when the schedule is unchanged since `expectedRevision` was read,
 * throws ScheduleConflictError when a phone patched it meanwhile.
 */
export async function assertScheduleRevision(
  client: ScheduleRpcClient,
  briefingId: string,
  expectedRevision: number,
): Promise<number> {
  const { data, error } = await client.rpc("assert_schedule_revision", {
    p_briefing_id: briefingId,
    p_expected: expectedRevision,
  });
  if (error) throw toWriteError(error);
  return z.number().int().parse(data);
}
